import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUserId } from 'hooks/useCurrentUser';
import { useToast } from 'hooks/useToast';
import { apiOrchestrator } from 'services/apiService';

interface SocialNetwork {
  id?: number;
  social_network_type: string;
  social_network_username: string;
}

// Custom event to notify updates
const USER_SOCIAL_NETWORKS_UPDATED = 'userSocialNetworksUpdated';

export const useUserSocialNetworks = () => {
  const { t } = useTranslation('toast');
  const userId = useUserId();
  const { toastError, toastSuccess } = useToast();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [socialNetworks, setSocialNetworks] = useState<SocialNetwork[]>([]);
  const [networkTypes, setNetworkTypes] = useState<string[]>([]);

  const handleApiError = useCallback(
    (error: any, defaultMessage: string, operation: string) => {
      const errorMessage = error.response?.data?.message || defaultMessage;
      setError(errorMessage);
      toastError(`${operation}: ${errorMessage}`);
    },
    [toastError]
  );

  const fetchNetworkTypes = useCallback(async () => {
    if (!userId) {
      handleApiError({}, t('socialNetwork.userNotAuthenticated'), t('socialNetwork.fetchTypes'));
      return;
    }

    try {
      const { data } = await apiOrchestrator.get<string[]>('/user-social-networks/types');
      if (!data || !Array.isArray(data)) {
        throw new Error(t('socialNetwork.invalidResponseFormat'));
      }
      setNetworkTypes(data);
      setError(null);
    } catch (error) {
      handleApiError(
        error,
        t('socialNetwork.failedToLoadTypes'),
        t('socialNetwork.fetchTypes')
      );
    }
  }, [userId, handleApiError, t]);

  const fetchSocialNetworks = useCallback(async () => {
    if (!userId) {
      handleApiError({}, t('socialNetwork.userNotAuthenticated'), t('socialNetwork.fetchNetworks'));
      return;
    }

    setLoading(true);
    try {
      const { data } = await apiOrchestrator.get<SocialNetwork[]>(
        `/user-social-networks/?user_id=${userId}`
      );
      if (!data || !Array.isArray(data)) {
        throw new Error(t('socialNetwork.invalidResponseFormat'));
      }
      setSocialNetworks(data);
      setError(null);
    } catch (error) {
      handleApiError(error, t('socialNetwork.failedToLoadNetworks'), t('socialNetwork.fetchNetworks'));
    } finally {
      setLoading(false);
    }
  }, [userId, handleApiError, t]);

  // Notify listeners about updates
  const notifyUpdate = useCallback(() => {
    if (!userId) return;
    const event = new CustomEvent(USER_SOCIAL_NETWORKS_UPDATED, {
      detail: { userId },
    });
    window.dispatchEvent(event);
  }, [userId]);

  const getUsernameByType = useCallback(
    (type: string): SocialNetwork | undefined => {
      if (!type) {
        handleApiError({}, t('socialNetwork.typeNotSpecified'), t('socialNetwork.fetchUser'));
        return undefined;
      }
      return socialNetworks.find(net => net.social_network_type === type);
    },
    [socialNetworks, handleApiError, t]
  );

  const upsertSocialNetwork = async (socialNetwork: SocialNetwork) => {
    if (!userId) {
      handleApiError({}, t('socialNetwork.userNotAuthenticated'), t('socialNetwork.updateNetwork'));
      return;
    }

    if (!socialNetwork.social_network_type) {
      handleApiError({}, t('socialNetwork.typeNotSpecified'), t('socialNetwork.dataValidation'));
      return;
    }

    const existingNetwork = socialNetworks.find(
      net => net.social_network_type === socialNetwork.social_network_type
    );

    // If the username is empty and a network exists, delete it
    if (
      !socialNetwork.social_network_username ||
      socialNetwork.social_network_username.trim() === ''
    ) {
      if (existingNetwork?.id) {
        // Delete the existing social network
        await deleteSocialNetwork(existingNetwork.id);
      }
      // If no network exists and username is empty, do nothing (valid)
      return;
    }

    try {
      if (existingNetwork) {
        await apiOrchestrator.patch(`/user-social-networks/${userId}`, {
          id: existingNetwork.id,
          ...socialNetwork,
        });
      } else {
        await apiOrchestrator.post(`/user-social-networks/${userId}`, socialNetwork);
      }

      // Refresh the social networks list
      await fetchSocialNetworks();

      // Notify that an update occurred
      notifyUpdate();

      toastSuccess(
        existingNetwork ? t('entity.social.updated') : t('entity.social.added')
      );
      setError(null);
    } catch (error) {
      handleApiError(
        error,
        t('error.operation', { action: existingNetwork ? t('actions.update') : t('actions.add'), entity: t('entities.socialNetwork') }),
        t('socialNetwork.updateNetwork')
      );
    }
  };

  const deleteSocialNetwork = async (networkId: number) => {
    if (!userId) {
      handleApiError({}, t('socialNetwork.userNotAuthenticated'), t('socialNetwork.removeNetwork'));
      return;
    }

    if (!networkId) {
      handleApiError({}, t('socialNetwork.networkIdMissing'), t('socialNetwork.dataValidation'));
      return;
    }

    const networkToDelete = socialNetworks.find(net => net.id === networkId);
    if (!networkToDelete) {
      handleApiError({}, t('socialNetwork.networkNotFound'), t('socialNetwork.dataValidation'));
      return;
    }

    try {
      await apiOrchestrator.delete(`/user-social-networks/${userId}/${networkId}`);

      // Refresh the social networks list
      await fetchSocialNetworks();

      // Notify that an update occurred
      notifyUpdate();

      toastSuccess(t('entity.social.removed'));
      setError(null);
    } catch (error) {
      handleApiError(
        error,
        t('error.operation', { action: t('actions.remove'), entity: t('entities.socialNetwork') }),
        t('socialNetwork.removeNetwork')
      );
    }
  };

  // Hook to listen for updates
  const useSocialNetworksUpdate = (callback: () => void) => {
    useEffect(() => {
      const handleUpdate = (event: CustomEvent) => {
        if (event.detail.userId === userId) {
          callback();
        }
      };

      window.addEventListener(USER_SOCIAL_NETWORKS_UPDATED, handleUpdate as EventListener);
      return () => {
        window.removeEventListener(USER_SOCIAL_NETWORKS_UPDATED, handleUpdate as EventListener);
      };
    }, [userId, callback]);
  };

  useEffect(() => {
    if (userId) {
      fetchNetworkTypes();
      fetchSocialNetworks();
    }
  }, [userId, fetchNetworkTypes, fetchSocialNetworks]);

  return {
    socialNetworks,
    networkTypes,
    loading,
    error,
    upsertSocialNetwork,
    deleteSocialNetwork,
    getUsernameByType,
    useSocialNetworksUpdate,
    refreshSocialNetworks: fetchSocialNetworks,
  };
};
